import { queryOptions } from '@tanstack/react-query';

import { $api, queueableApi } from '@shared/api';
import { routes } from '@shared/api/routes';
import { keysToCamel } from '@shared/helpers';

import { equipmentQueryKeys } from '../model/queryKeys';
import {
    AvailableProject,
    CreateWarehousePayload,
    EquipmentApiResponse,
    EquipmentData,
    EquipmentDataDto,
    MoveAllInventoryPayload,
    MoveFilteredInventoryPayload,
    MoveInventoryPayload,
    RenameWarehousePayload,
    SortWarehousePayload,
    Warehouse,
    WarehouseDto,
    WarehouseIdPayload,
    WarehouseListItem,
    WarehouseTotalInfo,
    WarehouseTotalInfoDto,
} from '../model/types';

const jsonHeaders = {
    'Content-Type': 'application/json;charset=utf-8',
};

const WAREHOUSE_ID_ROUTE_PARAM = '_warehouse_id_';
const PROJECT_ID_FIELD = 'project_id';

const buildWarehouseBody = (payload: CreateWarehousePayload) => {
    if (payload.projectId) {
        const body: Record<string, number> = {};
        body[PROJECT_ID_FIELD] = payload.projectId;

        return body;
    }

    return { title: payload.title };
};

export const equipmentApi = {
    getEquipmentData: ({ signal }: { signal?: AbortSignal } = {}) =>
        $api.get<EquipmentApiResponse<EquipmentDataDto>>(routes.loadEquipmentData(), {
            signal,
        }),
    getEquipmentDataQueryOptions: () =>
        queryOptions({
            queryKey: equipmentQueryKeys.data(),
            queryFn: (meta) => equipmentApi.getEquipmentData({ signal: meta.signal }),
            select: (data) => keysToCamel(data.data.response) as unknown as EquipmentData,
            refetchOnMount: true,
        }),

    getTotalInfo: ({ signal }: { signal?: AbortSignal } = {}) =>
        $api.get<EquipmentApiResponse<WarehouseTotalInfoDto>>(routes.getTotalInfo(), {
            signal,
        }),
    getTotalInfoQueryOptions: () =>
        queryOptions({
            queryKey: equipmentQueryKeys.totalInfo(),
            queryFn: (meta) => equipmentApi.getTotalInfo({ signal: meta.signal }),
            select: (data) => keysToCamel(data.data.response) as unknown as WarehouseTotalInfo,
        }),

    getWarehouseTotalInfo: ({
        warehouseId,
        signal,
    }: WarehouseIdPayload & { signal?: AbortSignal }) =>
        $api.get<EquipmentApiResponse<WarehouseTotalInfoDto>>(
            routes.getWarehouseTotalInfo({ [WAREHOUSE_ID_ROUTE_PARAM]: warehouseId }),
            { signal }
        ),
    getWarehouseTotalInfoQueryOptions: ({ warehouseId }: WarehouseIdPayload) =>
        queryOptions({
            queryKey: equipmentQueryKeys.warehouseTotalInfo(warehouseId),
            queryFn: (meta) =>
                equipmentApi.getWarehouseTotalInfo({ warehouseId, signal: meta.signal }),
            select: (data) => keysToCamel(data.data.response) as unknown as WarehouseTotalInfo,
        }),

    getWarehouseList: ({ signal }: { signal?: AbortSignal } = {}) =>
        $api.get<EquipmentApiResponse<WarehouseListItem[]>>(routes.warehouseList(), {
            signal,
        }),
    getWarehouseListQueryOptions: () =>
        queryOptions({
            queryKey: equipmentQueryKeys.warehouseList(),
            queryFn: (meta) => equipmentApi.getWarehouseList({ signal: meta.signal }),
            select: (data) => data.data.response,
        }),

    getAvailableProjects: ({ signal }: { signal?: AbortSignal } = {}) =>
        $api.get<EquipmentApiResponse<AvailableProject[]>>(
            routes.availableForAttachmentProjectList(),
            { signal }
        ),
    getAvailableProjectsQueryOptions: () =>
        queryOptions({
            queryKey: equipmentQueryKeys.availableProjects(),
            queryFn: (meta) => equipmentApi.getAvailableProjects({ signal: meta.signal }),
            select: (data) => keysToCamel(data.data.response) as unknown as AvailableProject[],
        }),

    createWarehouse: (payload: CreateWarehousePayload) =>
        $api.post<EquipmentApiResponse<WarehouseDto>>(
            routes.createWarehouse(),
            buildWarehouseBody(payload),
            { headers: jsonHeaders }
        ),
    renameWarehouse: ({ warehouseId, title }: RenameWarehousePayload) =>
        $api.post<EquipmentApiResponse<{ success: boolean }>>(
            routes.renameWarehouse({ [WAREHOUSE_ID_ROUTE_PARAM]: warehouseId }),
            { title },
            { headers: jsonHeaders }
        ),
    deleteWarehouse: ({ warehouseId }: WarehouseIdPayload) =>
        $api.delete<EquipmentApiResponse<{ success: boolean }>>(
            routes.deleteWarehouse({ [WAREHOUSE_ID_ROUTE_PARAM]: warehouseId }),
            { headers: jsonHeaders }
        ),
    moveAllInventory: ({ fromWarehouseId, toWarehouseId }: MoveAllInventoryPayload) =>
        queueableApi.addTask({
            data: {
                fromWarehouseId,
                toWarehouseId,
            },
            type: 'move-all-inventories',
        }),
    moveFilteredInventory: ({ inventoryGuids, toWarehouseId }: MoveFilteredInventoryPayload) =>
        queueableApi.addTask({
            data: {
                inventoriesGuidList: inventoryGuids,
                toWarehouseId,
            },
            type: 'move-part-of-inventories',
        }),
    moveInventory: ({ inventoryGuid, toWarehouseId, newIndex }: MoveInventoryPayload) =>
        queueableApi.addTask({
            data: {
                guid: inventoryGuid,
                toWarehouseId,
                newIndex,
            },
            type: 'sort-inventory',
        }),
    sortWarehouse: ({ warehouseId, newIndex }: SortWarehousePayload) =>
        queueableApi.addTask({
            data: {
                warehouseId,
                newIndex,
            },
            type: 'sort-warehouse',
        }),
    exportEquipmentUrl: () => routes.exportEquipment(),

    mapWarehouse: (warehouse: WarehouseDto) => keysToCamel(warehouse) as unknown as Warehouse,
};
